/*  ContentContext, client-side mirror of GET /api/content.
    Fetches once at app start; exposes merged content (defaults + server overrides)
    to every page. If the API is unreachable, we show the DEFAULT_CONTENT so nothing
    ever renders blank.

    The DEFAULT_CONTENT object below is kept in sync, verbatim, with
    /app/backend/data/default_content.py.
*/
import { createContext, useContext, useEffect, useState, useMemo } from "react";
import { fetchSiteContent } from "@/lib/api";

// ------------------------------------------------------------------
// DEFAULT_CONTENT, must mirror backend/data/default_content.py exactly
// ------------------------------------------------------------------
export const DEFAULT_CONTENT = {
    header: {
        siteName: "Holy Trinity Catholic Church",
        siteFullTitle: "Holy Trinity Catholic Church – Kileleshwa Parish Community",
        subtitle: "Kileleshwa Parish Community",
        archdiocese: "Archdiocese of Nairobi",
        motto: "Empowered and Sent",
        tagline: "A Community of Love, Unity and Service",
        acronym: "COLUS",
        location: "Kileleshwa",
    },
    home: {
        heroEyebrow: "Kileleshwa · Archdiocese of Nairobi",
        heroTitleLine1: "Empowered and Sent",
        heroTitleLine2: "A Community of Love, Unity and Service.",
        heroTagline: 'Marching towards a "We" Church.',
        heroWelcome:
            "Welcome to Holy Trinity Catholic Church, Kileleshwa Parish Community. Whether you are visiting, returning, or making this your parish home you belong here.",
        ctaMassTimes: "Mass Times",
        ctaWatchLive: "Watch Live",
        ctaPrayerWall: "Prayer Wall",
    },
    about: {
        eyebrow: "About Us · Archdiocese of Nairobi",
        heading: "A Community of Love, Unity and Service",
        mottoQuote: "Empowered and Sent",
        intro:
            "Holy Trinity Catholic Church, Kileleshwa Parish Community is a parish of the Archdiocese of Nairobi, located on Othaya Road, Kileleshwa. We gather around the Word and the Eucharist as the People of God and are sent out as missionary disciples.",
    },
    values: {
        heading: "Our Values, COLUS",
        intro:
            "Our Values capture our common beliefs, what we cherish and what we will always be known for. At HTCC, we are a Community of Love, Unity and Service, COLUS.",
        pillars: [
            { title: "Love", points: ["We are compassionate", "We are empathetic and caring", "We know one another"] },
            { title: "Unity", points: ["We pull together", "We walk together", "We support the Church"] },
            { title: "Service", points: ["We go the extra mile", "We share our talents, skills and time", "We are witnesses of God’s Word"] },
        ],
    },
    vision: {
        heading: "Our Vision",
        body:
            "Our Vision focuses on a Christ-centered, Spirit-led community that joyfully lives and spreads the Gospel, nurtures deep discipleship, and boldly responds to the changing needs of our parish community and the world.",
    },
    mission: {
        heading: "Our Mission",
        intro:
            "Our Mission Statement describes our purpose and our reason for existence. It captures our path to holiness: spiritual growth, self-sacrifice, apostolate, and contributing our part in society. Rooted in the love of Christ and guided by the Holy Spirit, we are a Catholic Community committed to:",
        commitments: [
            "Worshipping God in spirit and in truth through vibrant liturgy and sacramental life;",
            "Forming faithful disciples through on-going catechesis, evangelization and spiritual growth;",
            "Stewarding our gifts with gratitude, responsibility and generosity; and",
            "Responding to human needs with compassion, justice and hope.",
        ],
        closing:
            "Together, we strive to be a beacon of faith, unity and service in our diverse and growing neighbourhoods and beyond.",
        pledge:
            "To foster a spiritually enriching, apostolic and inclusive community where everyone grows in the Faith.",
    },
    footer: {
        brand: "Holy Trinity Catholic Church",
        brandLine2: "Kileleshwa Parish Community",
        brandLine3: "Archdiocese of Nairobi",
        mottoLine: "Empowered and Sent",
        taglineLine: 'Marching towards a "We" Church',
        address: "Othaya Road, Kileleshwa, Nairobi, Kenya",
        copyright: "© Holy Trinity Kileleshwa Parish. All rights reserved.",
    },
    contact: {
        deaneryLine: "Nairobi Deanery, Archdiocese of Nairobi",
        addressLine1: "Othaya Road, Kileleshwa",
        addressLine2: "P.O. Box 25732-00603, Nairobi, Kenya",
        phone: "0799 651 047",
        phone2: "",
        emailInfo: "info@holytrinitykileleshwa.org",
        emailPriest: "parishpriest@holytrinitykileleshwa.org",
    },
    paybill: {
        heading: "Church Paybill",
        paybillNumber: "308699",
        accountLabelTithe: "Tithe / Offertory",
        accountLabelOthers: "Others (Mass Intentions, etc.)",
        note: "Use the Church Paybill for tithe, offertory and Mass intentions.",
    },
    intentions: {
        heading: "Mass Intentions",
        body: "You may request a Holy Mass intention for loved ones or special occasions (such as Anniversaries, etc.). We accept requests for Sacramental Masses (such as Weddings and Baptisms) and Requiem (Funeral) Masses. Pay through the Church Paybill number below, then call the Parish Office to confirm your payment or make inquiries.",
    },
    confession: {
        line: "Confession is heard 30 minutes before each weekday Mass. You may also consult a priest after Mass or by appointment.",
    },
};

// Deep-merge helper: server overrides win, but any missing key falls back to defaults.
const deepMerge = (defaults, override) => {
    if (override == null) return defaults;
    if (Array.isArray(defaults)) return Array.isArray(override) ? override : defaults;
    if (typeof defaults !== "object") return override ?? defaults;
    const out = { ...defaults };
    for (const k of Object.keys(override || {})) {
        out[k] = deepMerge(defaults[k], override[k]);
    }
    return out;
};

const ContentContext = createContext({ content: DEFAULT_CONTENT, loading: true, refresh: () => {} });

export const ContentProvider = ({ children }) => {
    const [override, setOverride] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = () => {
        setLoading(true);
        fetchSiteContent()
            .then((server) => setOverride(server))
            .catch(() => setOverride(null)) // fall back silently to defaults
            .finally(() => setLoading(false));
    };

    useEffect(() => { refresh(); /* once on mount */ }, []);

    const value = useMemo(
        () => ({ content: deepMerge(DEFAULT_CONTENT, override), loading, refresh }),
        [override, loading]
    );

    return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => useContext(ContentContext);
